import { z } from 'zod/v4';

import { endpoint } from '../http/endpoint';
import { HttpEndpoint } from '../http/HttpEndpoint';
import {
    RuleBasedSelectionConditionDTORuntype,
    RuleBasedSelectionFallbackRuntype,
} from './ruleBasedSelectionBackendTypes';
import {
    ComparisonInputDTO,
    ComparisonResultRuntype,
    SourcingCalculationResponseRuntype,
    SourcingCalculationSummaryResponseRuntype,
    SourcingScenarioDTORuntype,
    SourcingScenarioPatchDTORuntype,
    SourcingScenarioPostDTORuntype,
} from './sourcingScenarioBackendTypes';

/** What reads a sourcing scenario's demands: order sizes, aggregate quantities and the calculation. */
export const scenarioDemandEndpointsToInvalidate: HttpEndpoint[] = [
    'GET /rfqs/:rfqId',
    'POST /sourcing-scenarios/bulk',
    'GET /calculations/scenario-combinations',
    'GET /calculations/calculation-assembly-costs',
    'GET /assemblies/:assemblyId/aggregate-quantity',
    'GET /panels',
    'POST /sourcing/calculation/read-selection',
    'POST /sourcing/calculation/summary',
    'GET /sourcing/calculation/line-items/:id',
    'GET /rfqs/:rfqId/demands',
];

const sourcingScenarioListEndpoints = {
    'POST /sourcing-scenarios/bulk': endpoint({
        description: 'Returns a list of sourcing scenarios given their ID',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({ ids: z.array(z.string()) }),
        responseBody: z.object({ items: z.array(SourcingScenarioDTORuntype) }),
    }),
};

const sourcingCalculationEndpoints = {
    'POST /sourcing/calculation/read-selection': endpoint({
        description: 'Returns the sourcing calculation',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            sourcing_scenario_ids: z.array(z.string()),
        }),
        responseBody: SourcingCalculationResponseRuntype,
    }),

    'POST /sourcing/calculation/summary': endpoint({
        description:
            'Returns the scenario-level fields of the sourcing calculation of every scenario in the demand group, without the line items',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            demand_group_id: z.string(),
        }),
        responseBody: SourcingCalculationSummaryResponseRuntype,
    }),

    'POST /sourcing/calculation/run-selection': endpoint({
        description: 'Runs offer selection for the given sourcing scenarios',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            sourcing_scenario_ids: z.array(z.string()),
        }),
        responseBody: z.string(),
        invalidates: [
            'GET /rfqs/:rfqId',
            'POST /sourcing/calculation/read-selection',
            'POST /sourcing/calculation/summary',
        ],
    }),

    'POST /sourcing/calculation/line-items/run-selection': endpoint({
        description: 'Clears selection and runs selection for a set of sourcing line items',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({ sourcing_line_items: z.array(z.uuid()) }),
        responseBody: z.string(),
        invalidates: [
            'GET /rfqs/:rfqId',
            'POST /sourcing/calculation/read-selection',
            'POST /sourcing/calculation/summary',
            'GET /sourcing/calculation/line-items/:id',
            'GET /sourcing-line-item/:id',
            'GET /sourcing-line-item/:id/solutions',
        ],
    }),
    'POST /sourcing/calculation/line-items/run-rule-based-selection': endpoint({
        description: 'Runs selection for a set of sourcing line items using conditions',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            sourcing_line_items: z.array(z.uuid()),
            condition: RuleBasedSelectionConditionDTORuntype,
            fallback: RuleBasedSelectionFallbackRuntype,
        }),
        responseBody: z.string(),
        invalidates: [
            'GET /rfqs/:rfqId',
            'POST /sourcing/calculation/read-selection',
            'POST /sourcing/calculation/summary',
            'GET /sourcing/calculation/line-items/:id',
            'GET /sourcing-line-item/:id',
            'GET /sourcing-line-item/:id/solutions',
        ],
    }),
};

const sourcingScenarioCrudEndpoints = {
    'POST /sourcing-scenarios': endpoint({
        description: 'Creates a new sourcing scenario',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: SourcingScenarioPostDTORuntype,
        responseBody: z.object({ data: SourcingScenarioDTORuntype }),
        invalidates: [
            'GET /rfqs/:rfqId',
            'POST /sourcing-scenarios/bulk',
            'GET /calculations/scenario-combinations',
            'GET /calculations/calculation-assembly-costs',
            'GET /assemblies/:assemblyId/aggregate-quantity',
            'GET /panels',
            'POST /sourcing/calculation/read-selection',
            'POST /sourcing/calculation/summary',
            // In a negotiation, creating a scenario also creates or joins a demand group.
            'POST /demand-groups/bulk',
            'GET /negotiation-v2',
            'GET /negotiation-v2/:id',
        ],
        removes: ['POST /sourcing-scenarios/bulk'],
    }),

    'PATCH /sourcing-scenarios/:sourcingScenarioId': endpoint({
        description: 'Updates a sourcing scenario',
        pathParams: z.object({ sourcingScenarioId: z.string() }),
        queryParams: z.undefined(),
        requestBody: SourcingScenarioPatchDTORuntype,
        responseBody: z.object({ data: SourcingScenarioDTORuntype }),
        invalidates: scenarioDemandEndpointsToInvalidate,
    }),

    'DELETE /sourcing-scenarios/:sourcingScenarioId': endpoint({
        description: 'Delete a sourcing scenario',
        pathParams: z.object({ sourcingScenarioId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.string(),
        invalidates: [
            'GET /rfqs/:rfqId',
            'POST /sourcing-scenarios/bulk',
            'GET /calculations/calculation-assembly-costs',
            'GET /assemblies/:assemblyId/aggregate-quantity',
            'GET /panels',
            'POST /sourcing/calculation/read-selection',
            'POST /sourcing/calculation/summary',
            // A demand group keeps the scenario in its own payload, so the
            // negotiation would otherwise go on listing a scenario that is gone
            // and 404 as soon as anything opens it.
            'POST /demand-groups/bulk',
            'GET /negotiation-v2',
            'GET /negotiation-v2/:id',
        ],
        removes: [
            'POST /sourcing-scenarios/bulk',
            'GET /calculations/scenario-combinations',
            'POST /sourcing/calculation/read-selection',
            'POST /sourcing/calculation/summary',
        ],
    }),
};

const sourcingScenarioActionEndpoints = {
    'POST /sourcing-scenarios/:sourcingScenarioId/load-api-offers': endpoint({
        description: 'Update api offers for a sourcing scenario',
        pathParams: z.object({ sourcingScenarioId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.string(),
        invalidates: ['GET /sourcing/progress/:rfqId'],
    }),

    'POST /sourcing-scenarios/:sourcingScenarioId/duplicate': endpoint({
        description: 'Duplicate a sourcing scenario',
        pathParams: z.object({ sourcingScenarioId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({
            name: z.string().min(1),
            // The default `LegacyWithDemandGroup` also copies the demand group; temporary, until all use demand groups.
            mode: z.enum(['LegacyWithDemandGroup', 'OnlySourcingScenario']).optional(),
        }),
        responseBody: z.object({ data: SourcingScenarioDTORuntype }),
        invalidates: [
            'GET /sourcing/progress/:rfqId',
            'GET /rfqs/:rfqId',
            'POST /sourcing-scenarios/bulk',
            'POST /sourcing/calculation/read-selection',
            'POST /sourcing/calculation/summary',
            // A duplicate creates a demand group or joins the source scenario's one, depending on `mode`.
            'POST /demand-groups/bulk',
            'GET /negotiation-v2',
            'GET /negotiation-v2/:id',
        ],
    }),

    'PATCH /sourcing-scenarios/:sourcingScenarioId/base': endpoint({
        description: 'Sets a sourcing scenario as the base scenario of its demand group',
        pathParams: z.object({ sourcingScenarioId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.string(),
        // Setting a base scenario can demote another one in the same demand group.
        invalidates: ['POST /sourcing-scenarios/bulk'],
    }),
};

type SourcingScenarioListEndpoints = typeof sourcingScenarioListEndpoints;
type SourcingCalculationEndpoints = typeof sourcingCalculationEndpoints;
type SourcingScenarioCrudEndpoints = typeof sourcingScenarioCrudEndpoints;
type SourcingScenarioActionEndpoints = typeof sourcingScenarioActionEndpoints;

const sourcingScenarioCompareEndpoints = {
    'POST /sourcing/calculation/compare': endpoint({
        description:
            'Compares a baseline sourcing scenario with reference scenarios and returns a 2D matrix of compared line items.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.custom<ComparisonInputDTO>(),
        responseBody: ComparisonResultRuntype,
    }),
};

type SourcingScenarioCompareEndpoints = typeof sourcingScenarioCompareEndpoints;

interface SourcingScenarioEndpoints
    extends
        SourcingScenarioListEndpoints,
        SourcingCalculationEndpoints,
        SourcingScenarioCrudEndpoints,
        SourcingScenarioActionEndpoints,
        SourcingScenarioCompareEndpoints {}

// Split into groups with an explicit type: the inferred one exceeds what TypeScript can serialize.
export const sourcingScenarioEndpoints: SourcingScenarioEndpoints = {
    ...sourcingScenarioListEndpoints,
    ...sourcingCalculationEndpoints,
    ...sourcingScenarioCrudEndpoints,
    ...sourcingScenarioActionEndpoints,
    ...sourcingScenarioCompareEndpoints,
};
